import { useMutation, useQuery, useQueryClient } from "react-query";
import { AuthContext } from "../../../../../contexts/AuthContext";
import {
  addAdditionalCredits,
  cancelCoaching,
  getUsedCreditsAmount,
  getUserBudgets,
  getUserCredits,
} from "../../../../../services/coaching.service";
import { MyAreaTitle } from "../components";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { CircularImage } from "../../../../../components/circularImage";
import Button from "../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";
import { PopupWrapper } from "../../../../../components/popupWrapper";
import { ReactComponent as Close } from "../../../../../assets/close.svg";
import { ReactComponent as MoreOptions } from "../../../../../assets/more_options.svg";
import {
  useArrayOutsideClickDetector,
  useOutsideClickDetector,
} from "../../../../../hooks/useOutsideClickDetector";
import { useNavigate } from "react-router-dom";
import { cancelWebinar } from "../../../../../services/webinar.service";
import AddCreditsModal from "./AddCreditsModal";
import { CreditCode } from "../utils";
import BudgetDisplay from "./BudgeDisplay";

export const getCreditDisplayString = (credit: any, t: any) => {
  const coachFullName =
    credit.coachName && credit.coachSurname
      ? `${credit.coachName} ${credit.coachSurname}`
      : t("credits.unknownCoach", "Unknown Coach");

  switch (credit.code) {
    case CreditCode.LINKEDINPACKAGE:
      return (
        <>
          {coachFullName}
          <br />({t("credits.linkedinPackage")})
        </>
      );

    case CreditCode.LINC:
      return (
        <>
          {coachFullName}
          <br />({t("credits.linc")})
        </>
      );

    case CreditCode.CV_CHECK:
      if (credit.creditsPerCoaching === 0) {
        return t("credits.cv-check-basic", "CV Check Basic");
      } else {
        return (
          <>
            {coachFullName}
            <br />({t("credits.cv-check-plus")})
          </>
        );
      }

    case CreditCode.EXTERNAL_SERVICES:
      return (
        credit.externalServiceCredit?.service ||
        t("credits.externalService", "External Service")
      );

    case CreditCode.WEBINAR:
      if (credit.webinarTitle && credit.webinarTitle.trim() !== "") {
        const prefix = t("credits.webinarPrefix", "Webinar:");
        const title = credit.webinarTitle;
        return `${prefix} ${
          title.length > 30 ? `${title.slice(0, 30)}...` : title
        }`;
      } else {
        return t("credits.webinar-unpublished");
      }

    case CreditCode.MANUAL:
      const description =
        credit.description || t("credits.manualEntry", "Manual Entry");
      return description.length > 30
        ? `${description.slice(0, 30)}...`
        : description;

    default:
      return coachFullName;
  }
};

function CreditsCounter({
  creditsAmount,
  setCreditsAmount,
}: {
  creditsAmount: number;
  setCreditsAmount: (amount: number) => void;
}) {
  const { user } = useContext(AuthContext);

  const { t } = useTranslation("myArea");

  const { refetch } = useQuery(["usedCreditsAmount"], getUsedCreditsAmount, {
    onSuccess: (data) => {
      setCreditsAmount(data);
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (creditsAmount !== undefined) {
      refetch();
    }
  }, [creditsAmount, refetch]);

  return (
    <div className="flex flex-col w-full rounded-md bg-Grey-light-3 p-6">
      <div className="flex justify-between items-center w-full">
        <div className="h-3 rounded-md bg-Grey-dark w-full relative">
          <div
            className="h-3 rounded-md bg-Yellow-default absolute left-0 top-0"
            style={{
              width: `${
                user?.usedVoucher?.maxCredits
                  ? (
                      (creditsAmount / user?.usedVoucher?.maxCredits) *
                      100
                    ).toFixed(0)
                  : 0
              }%`,
            }}
          ></div>
        </div>
      </div>
      <div className="flex justify-between mt-2 font-bold text-Grey-dark text-sm">
        <div>{t("credits.used-credits")}</div>
        <div>
          {creditsAmount}/{user?.usedVoucher?.maxCredits}
        </div>
      </div>
    </div>
  );
}

export function CreditsTable({
  credits,
  refetchCredits,
  setCreditsAmount,
}: {
  credits: any;
  refetchCredits: () => void;
  setCreditsAmount: (v: number) => void;
}) {
  const { t } = useTranslation("myArea");
  const [cancelCreditSelected, setCancelCreditSelected] = useState<any | null>(
    null
  );
  const [activeCreditId, setActiveCreditId] = useState<number | null>(null);
  const getUserTimeZone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { user } = useContext(AuthContext);
  const [showOverlay, setShowOverlay] = useState(false);
  const [moreOptions, setMoreOptions] = useState<number | null>(null);
  const [successfullyCanceled, setSuccessfullyCanceled] = useState(false);
  const [successfullyAdditionalCredits, setSuccessfullyAdditionalCredits] =
    useState(false);

  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const moreOptionsRef = useRef<any>(null);
  const cancelButtonRef = useRef<any>(null);

  useArrayOutsideClickDetector([moreOptionsRef, cancelButtonRef], () =>
    setMoreOptions(null)
  );

  const handleMoreOptionsClick = (index: any) => {
    if (moreOptions === index) {
      setMoreOptions(null);
    } else {
      setMoreOptions(index);
    }
  };

  const handleAddCreditsClick = (credit: any) => {
    setActiveCreditId(credit);
    setShowOverlay(true);
  };
  const queryClient = useQueryClient();

  const additionalCreditsMutation = useMutation(
    ({ creditId, creditsToAdd }: { creditId: number; creditsToAdd: number }) =>
      addAdditionalCredits(creditId, creditsToAdd),
    {
      onSuccess: () => {
        setSuccessfullyAdditionalCredits(true);
        queryClient.invalidateQueries(["usedCreditsAmount"]);
        setTimeout(() => {
          setSuccessfullyAdditionalCredits(false);
          setActiveCreditId(null);
          refetchCredits();
        }, 2000);
        refetchCredits();
      },
      onError: (error) => {
        // Handle error
        console.error("Error adding credits:", error);
      },
    }
  );

  const handleAddCredits = (creditsToAdd: number) => {
    if (activeCreditId)
      additionalCreditsMutation.mutate({
        creditId: activeCreditId,
        creditsToAdd: creditsToAdd,
      });
  };

  const handleCloseModal = () => {
    setShowOverlay(false);
    setActiveCreditId(null);
  };

  const calculateCreditIncrements = (
    minuteCost: number, // Cost per minute
    availableCredits: number, // Credits the user currently has available
    coachingCostSoFar: number, // Credits used so far
    realCoachingCost: number // Initial coaching cost (the baseline)
  ) => {
    const increments = [];

    // Maximum number of credits the user can add
    const maxCredits = 2 * realCoachingCost - coachingCostSoFar;

    // Ensure maxCredits is at least 0 to prevent negative increments
    const validMaxCredits = Math.max(0, maxCredits);

    // If minuteCost is 0, calculate increments based on the maximum credits
    if (minuteCost === 0 || minuteCost === undefined) {
      const minutesPerCredit = 60 / coachingCostSoFar;
      for (let i = 1; i <= validMaxCredits; i++) {
        increments.push({ value: i, minutes: i * minutesPerCredit });
      }
    } else {
      // minuteCost is provided and > 0
      for (let i = 1; i <= validMaxCredits; i++) {
        increments.push({ value: i, minutes: i * minuteCost });
      }
    }

    // Filter increments based on available credits if needed
    return increments.filter((inc) => inc.value <= availableCredits);
  };

  const { data: usedCreditsAmount } = useQuery(
    ["usedCreditsAmount"],
    getUsedCreditsAmount,
    {
      enabled: successfullyCanceled,
      onSuccess: (data) => setCreditsAmount(data),
    }
  );

  const availableCredits =
    (user?.usedVoucher?.maxCredits || 0) - (usedCreditsAmount || 0);

  const cancelCoachingMutation = useMutation(
    ["cancelCoaching"],
    () => cancelCoaching({ creditId: cancelCreditSelected?.id || 0, message }),
    {
      onSuccess: () => {
        setSuccessfullyCanceled(true);
        setTimeout(() => {
          setSuccessfullyCanceled(false);
          setCancelCreditSelected(null);
          setMessage("");
          refetchCredits();
        }, 2000);
      },
    }
  );

  const cancelWebinarMutation = useMutation(
    ["cancelCoaching"],
    () =>
      cancelWebinar({
        webinarId: String(cancelCreditSelected?.webinarId || 0),
        userTimeZone: getUserTimeZone(),
      }),
    {
      onSuccess: () => {
        setSuccessfullyCanceled(true);
        setTimeout(() => {
          setSuccessfullyCanceled(false);
          setCancelCreditSelected(null);
          setMessage("");
          refetchCredits();
        }, 2000);
      },
    }
  );

  const now = new Date();
  const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  const handleCreditClick = (contentId: string, code: string) => {
    if (code === CreditCode.WEBINAR) {
      navigate(`/community/webinars/${contentId}`);
    }
    // else {
    //   navigate(`/coaches/${contentId}/overview`);
    // }
  };

  return (
    <>
      <PopupWrapper isOpen={!!cancelCreditSelected}>
        <div className="bg-white bg-opacity-100 md:w-1/3 w-1/2 p-5 rounded-lg">
          {successfullyCanceled ? (
            <div>
              {cancelCreditSelected?.code === CreditCode.WEBINAR
                ? t("credits.webinar-success-cancelation-message")
                : t("credits.success-cancelation-message")}
            </div>
          ) : (
            <>
              <div className="flex mb-4 justify-between items-center text-lg font-bold">
                <div className="w-10/12">
                  {cancelCreditSelected?.code === CreditCode.WEBINAR ? (
                    <>
                      {t("credits.cancel-confirmation-webinar")}{" "}
                      <span className="font-bold">
                        {cancelCreditSelected?.coachName}{" "}
                        {cancelCreditSelected?.coachSurname}
                      </span>
                    </>
                  ) : (
                    <>
                      {t("credits.cancel-confirmation")} {t("credits.with")}{" "}
                      <span className="font-bold">
                        {cancelCreditSelected?.coachName}{" "}
                        {cancelCreditSelected?.coachSurname}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex justify-center w-2/12 ">
                  <Close
                    className="cursor-pointer"
                    onClick={() => setCancelCreditSelected(null)}
                  />
                </div>
              </div>
              <div className="text-sm text-Grey-dark mb-4">
                {cancelCreditSelected?.code === CreditCode.WEBINAR
                  ? t("credits.cancel-warning-webinar")
                  : t("credits.cancel-warning")}
              </div>
              {cancelCreditSelected?.code === CreditCode.WEBINAR ? (
                <div className="mb-6"></div>
              ) : (
                <textarea
                  value={message}
                  placeholder={
                    cancelCreditSelected?.code === CreditCode.WEBINAR
                      ? (t("credits.webinar-inform-placeholder") as string)
                      : (t("credits.inform-placeholder") as string)
                  }
                  onChange={(e: any) => setMessage(e.target.value)}
                  className="w-full rounded-md h-32 mb-5"
                />
              )}
              <div className="flex justify-between">
                <Button
                  className="w-full mr-2"
                  variant={ButtonVariant.SECONDARY}
                  size={ButtonSize.MEDIUM}
                  onClick={() => setCancelCreditSelected(null)}
                >
                  {t("credits.cancel-button")}
                </Button>
                <Button
                  className="w-full ml-2"
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  onClick={() =>
                    cancelCreditSelected?.code === CreditCode.WEBINAR
                      ? cancelWebinarMutation.mutate()
                      : cancelCoachingMutation.mutate()
                  }
                >
                  {t("credits.confirm-button")}
                </Button>
              </div>
            </>
          )}
        </div>
      </PopupWrapper>
      {credits?.length > 0 ? (
        <>
          <table className="table-auto w-full hidden md:table ">
            <thead className="w-full border-b">
              <tr>
                <th className="text-left pb-2">
                  {t("credits.table-headers.coach")}
                </th>
                <th className="text-left pb-2 px-2">
                  {t("credits.table-headers.request-date")}
                </th>
                <th className="text-left pb-2 px-2">
                  {t("credits.table-headers.status")}
                </th>
                <th className="text-left pb-2 px-2">
                  {t("credits.table-headers.credits")}
                </th>
                <th className="text-right pb-2 px-2">
                  {t("credits.table-headers.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {credits?.map((c: any, idx: number) => {
                const startDate = new Date(c.startDate);
                const shouldRenderCancelButton =
                  c.code !== CreditCode.WEBINAR ||
                  startDate >= twentyFourHoursFromNow;
                const shouldShowAdditionalCreditsButton =
                  availableCredits > 0 &&
                  2 * c.coachingCostContentful - c.creditsPerCoaching > 0 &&
                  c.code !== CreditCode.WEBINAR;

                return (
                  <tr key={c.id} className="w-full border-b">
                    <td>
                      <div className="flex items-center py-3">
                        {((c.code === CreditCode.CV_CHECK &&
                          c.creditsPerCoaching !== 0) ||
                          (c.code !== CreditCode.EXTERNAL_SERVICES &&
                            c.code !== CreditCode.WEBINAR &&
                            c.code !== CreditCode.MANUAL &&
                            c.code !== CreditCode.CV_CHECK)) && (
                          <div className="w-9 h-9 min-w-9 min-h-9 mr-2">
                            <CircularImage src={c.coachProfilePicture} />
                          </div>
                        )}
                        <span
                          onClick={
                            c.code !== CreditCode.CV_CHECK &&
                            c.code !== CreditCode.EXTERNAL_SERVICES &&
                            c.code !== CreditCode.MANUAL &&
                            c.webinarTitle !== ""
                              ? () =>
                                  handleCreditClick(
                                    c.code === CreditCode.WEBINAR
                                      ? c.webinarId
                                      : c.coachId,
                                    c.code
                                  )
                              : undefined
                          }
                          className={
                            c.code !== CreditCode.CV_CHECK &&
                            c.code !== CreditCode.EXTERNAL_SERVICES &&
                            c.code !== CreditCode.MANUAL &&
                            c.webinarTitle !== ""
                              ? ""
                              : ""
                          }
                        >
                          {getCreditDisplayString(c, t)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="px-2">{c.createdAt.split("T")[0]}</span>
                    </td>
                    <td>
                      <span className="px-2">
                        {t(`credits.status.${c.status}`)}
                      </span>
                    </td>
                    <td>
                      <span className="px-6">{c.creditsPerCoaching}</span>
                    </td>
                    <td className="flex justify-end items-center p-2">
                      <div className="relative flex flex-col justify-center items-center mt-2 mb-2 space-y-2">
                        {c.code !== CreditCode.EXTERNAL_SERVICES &&
                          c.code !== CreditCode.MANUAL && (
                            <>
                              {c.status === "Redeemed" && (
                                <>
                                  {/* Render feedback button if coachFeedbackLink exists */}
                                  {c.coachFeedbackLink && (
                                    <Button
                                      className="w-full"
                                      variant={ButtonVariant.PRIMARY}
                                      size={ButtonSize.SMALL}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          c.coachFeedbackLink,
                                          "_blank"
                                        );
                                      }}
                                    >
                                      {t("credits.feedback-button")}
                                    </Button>
                                  )}
                                  {/* Render Add Credits button */}

                                  {(!c.coachFeedbackLink ||
                                    c.code === CreditCode.WEBINAR) && (
                                    <div className="text-Grey">
                                      {t("credits.no-available-actions")}
                                    </div>
                                  )}
                                </>
                              )}
                              {c.status !== "Redeemed" &&
                                c.status !== "Blocked" && (
                                  <div className="text-Grey">
                                    {t("credits.no-available-actions")}
                                  </div>
                                )}
                              {c.status === "Blocked" && (
                                <>
                                  {shouldRenderCancelButton &&
                                    c.code !== CreditCode.CV_CHECK &&
                                    c.code !== CreditCode.LINC && (
                                      <Button
                                        className="w-full"
                                        variant={ButtonVariant.PRIMARY}
                                        size={ButtonSize.SMALL}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCancelCreditSelected(c);
                                        }}
                                      >
                                        {t("credits.cancel-button")}
                                      </Button>
                                    )}
                                  {shouldShowAdditionalCreditsButton && (
                                    <Button
                                      className="w-full"
                                      variant={ButtonVariant.PRIMARY}
                                      size={ButtonSize.SMALL}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddCreditsClick(c.id);
                                      }}
                                    >
                                      {t("credits.add-credits-button")}
                                    </Button>
                                  )}
                                  {c.code === CreditCode.WEBINAR &&
                                    !shouldRenderCancelButton && (
                                      <div className="text-Grey">
                                        {t("credits.no-available-actions")}
                                      </div>
                                    )}
                                </>
                              )}
                            </>
                          )}
                        {c.code === CreditCode.MANUAL && (
                          <div className="text-Grey">
                            {t("credits.no-available-actions")}
                          </div>
                        )}
                      </div>
                    </td>
                    {activeCreditId === c.id && availableCredits > 0 && (
                      <AddCreditsModal
                        isOpen={showOverlay}
                        onClose={handleCloseModal}
                        onAddCredits={handleAddCredits}
                        availableCredits={availableCredits}
                        creditsIncrements={calculateCreditIncrements(
                          c.minuteCost,
                          availableCredits,
                          c.creditsPerCoaching,
                          c.coachingCostContentful
                        )}
                        successfullyAdditionalCredits={
                          successfullyAdditionalCredits
                        }
                      />
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="md:hidden">
            {credits?.map((c: any, idx: number) => {
              const startDate = new Date(c.startDate);
              const shouldRenderCancelButton =
                c.code !== CreditCode.WEBINAR ||
                startDate >= twentyFourHoursFromNow;
              const shouldShowAdditionalCreditsButton =
                availableCredits > 0 &&
                2 * c.coachingCostContentful - c.creditsPerCoaching > 0 &&
                c.code !== CreditCode.WEBINAR;
              return (
                <>
                  <div className="mb-4 flex justify-between">
                    <div>
                      <div className="flex items-center mb-1">
                        {((c.code === CreditCode.CV_CHECK &&
                          c.creditsPerCoaching !== 0) ||
                          (c.code !== CreditCode.EXTERNAL_SERVICES &&
                            c.code !== CreditCode.WEBINAR &&
                            c.code !== CreditCode.MANUAL &&
                            c.code !== CreditCode.CV_CHECK)) && (
                          <div className="w-9 h-9 min-w-9 min-h-9 mr-2">
                            <CircularImage src={c.coachProfilePicture} />
                          </div>
                        )}

                        <span
                          onClick={
                            c.code !== CreditCode.CV_CHECK &&
                            c.code !== CreditCode.EXTERNAL_SERVICES &&
                            c.code !== CreditCode.MANUAL
                              ? () =>
                                  handleCreditClick(
                                    c.code === CreditCode.WEBINAR
                                      ? c.webinarId
                                      : c.coachId,
                                    c.code
                                  )
                              : undefined
                          }
                          className={
                            c.code === CreditCode.WEBINAR &&
                            c.code !== CreditCode.EXTERNAL_SERVICES &&
                            c.code !== CreditCode.MANUAL
                              ? ""
                              : ""
                          }
                        >
                          {getCreditDisplayString(c, t)}
                        </span>
                      </div>
                      <div className="text-grey">
                        <span className="mr-2">
                          {t("credits.table-headers.status")}:
                        </span>
                        <span>{t(`credits.status.${c.status}`)}</span>
                      </div>
                      <div className="text-Grey">
                        <span className="mr-2">
                          {t("credits.table-headers.request-date")}:
                        </span>
                        <span>{c.createdAt.split("T")[0]}</span>
                      </div>
                      <div className="text-Grey">
                        <span className="mr-2">
                          {t("credits.table-headers.credits")}:
                        </span>
                        <span>{c.creditsPerCoaching}</span>
                      </div>
                    </div>
                    <div className="relative flex flex-col mt-2 mb-2 space-y-2">
                      {c.code !== CreditCode.EXTERNAL_SERVICES &&
                        c.code !== CreditCode.MANUAL && (
                          <>
                            {c.status === "Redeemed" && (
                              <>
                                {/* Render feedback button if coachFeedbackLink exists */}
                                {c.coachFeedbackLink && (
                                  <Button
                                    className="w-full"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        c.coachFeedbackLink,
                                        "_blank"
                                      );
                                    }}
                                  >
                                    {t("credits.feedback-button")}
                                  </Button>
                                )}
                                {/* Render Add Credits button */}

                                {(!c.coachFeedbackLink ||
                                  c.code === CreditCode.WEBINAR) && (
                                  <div className="text-Grey">
                                    {t("credits.no-available-actions")}
                                  </div>
                                )}
                              </>
                            )}
                            {c.status !== "Redeemed" &&
                              c.status !== "Blocked" && (
                                <div className="text-Grey">
                                  {t("credits.no-available-actions")}
                                </div>
                              )}
                            {c.status === "Blocked" && (
                              <>
                                {shouldRenderCancelButton &&
                                  c.code !== CreditCode.CV_CHECK && (
                                    <Button
                                      className="w-full"
                                      variant={ButtonVariant.PRIMARY}
                                      size={ButtonSize.SMALL}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCancelCreditSelected(c);
                                      }}
                                    >
                                      {t("credits.cancel-button")}
                                    </Button>
                                  )}
                                {shouldShowAdditionalCreditsButton && (
                                  <Button
                                    className="w-full"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddCreditsClick(c.id);
                                    }}
                                  >
                                    {t("credits.add-credits-button")}
                                  </Button>
                                )}
                                {c.code === CreditCode.WEBINAR &&
                                  !shouldRenderCancelButton && (
                                    <div className="text-Grey">
                                      {t("credits.no-available-actions")}
                                    </div>
                                  )}
                              </>
                            )}
                          </>
                        )}
                      {c.code === CreditCode.MANUAL && (
                        <div className="text-Grey">
                          {t("credits.no-available-actions")}
                        </div>
                      )}
                      {activeCreditId === c.id && availableCredits > 0 && (
                        <AddCreditsModal
                          isOpen={showOverlay}
                          onClose={handleCloseModal}
                          onAddCredits={handleAddCredits}
                          availableCredits={availableCredits}
                          refetchCredits={refetchCredits}
                          creditsIncrements={calculateCreditIncrements(
                            c.minuteCost,
                            availableCredits,
                            c.creditsPerCoaching,
                            c.coachingCostContentful
                          )}
                          successfullyAdditionalCredits={
                            successfullyAdditionalCredits
                          }
                        />
                      )}
                    </div>
                  </div>
                  {idx + 1 < credits?.length && (
                    <hr className="border-1 border-Grey-light-2 mb-4" />
                  )}
                </>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-Grey">{t("credits.no-credits-used")}</div>
      )}
    </>
  );
}

interface BudgetData {
  budget: number;
  remainingBudget: number;
  budgetList: any[];
}

export function Credits() {
  const { t } = useTranslation("myArea");

  const PAGE_INCREMENT = 3;
  const { configurations, user } = useContext(AuthContext);

  const userHasBudget =
    Boolean(user?.budget) &&
    !isNaN(parseFloat(user?.budget)) &&
    parseFloat(user?.budget) !== 0;

  const pageTitle = userHasBudget ? "credits.titleWithBudget" : "credits.title";

  const [credits, setCredits] = useState<any[]>([]);

  const [allCreditsLoaded, setAllCreditsLoaded] = useState(false);
  const [allBudgetLoaded, setAllBudgetLoaded] = useState(false);
  const [budgetData, setBudgetData] = useState<BudgetData | null>(null);

  const [creditsAmount, setCreditsAmount] = useState(0);
  const [creditsPageSize, setCreditsPageSize] = useState(PAGE_INCREMENT);
  const [budgetPageSize, setBudgetPageSize] = useState(PAGE_INCREMENT);

  const fetchSpecifiedAmountOfUserCredits = () => {
    return getUserCredits(creditsPageSize);
  };

  const fetchSpecifiedAmountOfUserBudgets = () => {
    return getUserBudgets(budgetPageSize);
  };

  const { refetch: refetchCredits } = useQuery(
    ["credits", creditsPageSize],
    fetchSpecifiedAmountOfUserCredits,
    {
      onSuccess: (data) => {
        setCredits(data.credits);
        setAllCreditsLoaded(data.all);
      },
    }
  );

  const { refetch: refetchBudget } = useQuery(
    ["budget", budgetPageSize],
    fetchSpecifiedAmountOfUserBudgets,
    {
      onSuccess: (data) => {
        setAllBudgetLoaded(data.all);
        if (data && data.budgetList) {
          setBudgetData({
            budget: data?.budget,
            remainingBudget: data?.remainingBudget,
            budgetList: data.budgetList,
          });
        }
      },
    }
  );

  return (
    <div>
      <div className="hidden lg:flex">
        <MyAreaTitle title={t(`${pageTitle}`)} />
      </div>
      <CreditsCounter
        creditsAmount={creditsAmount}
        setCreditsAmount={setCreditsAmount}
      />
      <div className="mt-10 ">
        <div className="font-bold text-Grey-dark mb-7">
          {t("credits.transaction-history")}
        </div>
        <div className=" bg-Grey-light-3 rounded-md p-4">
          <CreditsTable
            setCreditsAmount={setCreditsAmount}
            refetchCredits={refetchCredits}
            credits={credits}
          />{" "}
        </div>
        {!allCreditsLoaded ? (
          <Button
            variant={ButtonVariant.SECONDARY}
            className="p-1 mt-5 text-Black border-Black"
            onClick={() =>
              setCreditsPageSize(
                (creditsAmount) => creditsAmount + PAGE_INCREMENT
              )
            }
          >
            {t("credits.show-more")}
          </Button>
        ) : null}
      </div>

      {userHasBudget && budgetData?.budget && (
        <>
          <div className="mt-10 ">
            <div className="font-bold text-Grey-dark mb-7">
              {t("credits.your-budget")}
            </div>
            <BudgetDisplay budgetData={budgetData} />

            {!allBudgetLoaded ? (
              <Button
                variant={ButtonVariant.SECONDARY}
                className="p-1 mt-5 text-Black border-Black"
                onClick={() =>
                  setBudgetPageSize(
                    (budgetAmount) => budgetAmount + PAGE_INCREMENT
                  )
                }
              >
                {t("credits.show-more")}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
